/**
 * MKS-493 — ThemeProvider + useTheme().
 *
 * The app had no theme mechanism at all before this (no `data-theme`, no
 * provider, no dark mode), so this is the minimal one: the provider writes
 * `document.documentElement.dataset.theme`, and `src/styles/sparkle.css`
 * hangs every Sparkle rule off `[data-theme="sparkle"]`.
 *
 * ## Resolution order
 *
 *   1. The `theme` key in the preferences JSONB (the durable, cross-device
 *      answer), once `GET /auth/preferences` has resolved.
 *   2. `localStorage['ms-theme']`, which is what makes the FIRST paint of the
 *      next session correct: the preferences query takes a round trip, and
 *      without this mirror every reload would flash the default theme first.
 *   3. `default`.
 *
 * `setTheme` writes BOTH stores, so (1) and (2) cannot drift apart.
 *
 * Mounted INSIDE `AuthProvider` (see `main.tsx`) because it reads the
 * signed-in user's preferences.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { usePreferences, useUpdatePreferences } from '../hooks/usePreferences';

export const THEMES = ['default', 'sparkle'] as const;
export type Theme = (typeof THEMES)[number];

export const DEFAULT_THEME: Theme = 'default';

/** The localStorage mirror. Named in the contract, so it is a stable key. */
export const THEME_STORAGE_KEY = 'ms-theme';

export interface ThemeContextValue {
  /** The theme actually applied to `<html>` right now. */
  theme: Theme;
  /** Persists to the preferences JSONB and to localStorage. */
  setTheme: (next: Theme) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

/** Narrow an unknown value to a Theme, or `null` if it is not one. */
export function parseTheme(value: unknown): Theme | null {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value)
    ? (value as Theme)
    : null;
}

/** Read the localStorage mirror. Private-mode Safari throws on access. */
function readStoredTheme(): Theme | null {
  try {
    return parseTheme(window.localStorage.getItem(THEME_STORAGE_KEY));
  } catch {
    return null;
  }
}

function writeStoredTheme(next: Theme): void {
  try {
    window.localStorage.setItem(THEME_STORAGE_KEY, next);
  } catch {
    // Storage disabled or full — the preferences JSONB is still authoritative,
    // the only loss is a default-themed first paint on the next load.
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  // The same cached `['preferences']` query the sidebar and the boards already
  // subscribe to, so this adds no extra request.
  const { data: preferences } = usePreferences();
  const updatePreferences = useUpdatePreferences();

  const theme: Theme = useMemo(
    () => parseTheme(preferences?.theme) ?? readStoredTheme() ?? DEFAULT_THEME,
    [preferences?.theme]
  );

  // Keep the localStorage mirror in step with whatever the server said, so a
  // theme picked on another device paints correctly here on the NEXT load
  // rather than only after its preferences query resolves.
  useEffect(() => {
    const fromServer = parseTheme(preferences?.theme);
    if (fromServer) writeStoredTheme(fromServer);
  }, [preferences?.theme]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.theme = theme;
    return () => {
      // Provider unmounting (tests, hot reload) must not leave the app pink.
      delete root.dataset.theme;
    };
  }, [theme]);

  const setTheme = useCallback(
    (next: Theme) => {
      // localStorage first and synchronously: it is what the next first paint
      // reads, and it must be right even if the PATCH is still in flight or
      // fails outright.
      writeStoredTheme(next);
      updatePreferences.mutate({ theme: next });
    },
    [updatePreferences]
  );

  const value = useMemo<ThemeContextValue>(() => ({ theme, setTheme }), [theme, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

const FALLBACK: ThemeContextValue = {
  theme: DEFAULT_THEME,
  setTheme: () => {},
};

/**
 * The theme context, or a default-theme fallback when no `ThemeProvider` is
 * mounted. The fallback is what keeps every existing component test — which
 * renders a component in isolation, with no providers — on the default theme.
 */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext) ?? FALLBACK;
}
